import { styled } from '@linaria/react';
import { Group, Stack } from '@mantine/core';
import { BaseEdge, EdgeLabelRenderer, EdgeProps, getStraightPath } from '@xyflow/react';
import { purpleDarkest } from '../../../../styles/design-tokens';
import { Text } from '../../../../ui-library/typography/typography';
import { agentSourcesIcon } from '../../agentic-process-helpers';
import type { SessionMetadata } from '../../agentic-process.type';

interface SessionEdgeData {
  session?: SessionMetadata;
  toggleSessionCollapse?: (sessionId: string) => void;
}

interface SessionEdgeProps extends Omit<EdgeProps, 'data'> {
  data?: SessionEdgeData;
}

export const SessionEdge = ({ sourceX, sourceY, targetX, targetY, markerEnd, data }: SessionEdgeProps) => {
  const session = data?.session;
  const color = purpleDarkest;
  const toggleSessionCollapse = data?.toggleSessionCollapse;

  const [edgePath, labelX] = getStraightPath({
    sourceX,
    sourceY,
    targetX,
    targetY,
  });

  const handleClick = () => {
    if (toggleSessionCollapse && session?.sessionId) {
      toggleSessionCollapse(session.sessionId);
    }
  };

  const agentSourceIcon = session?.agentSource ? agentSourcesIcon(session.agentSource, 0, 16) : null;
  return (
    <>
      <BaseEdge
        path={edgePath}
        markerEnd={markerEnd}
        style={{
          stroke: color,
          strokeWidth: 4,
          cursor: toggleSessionCollapse ? 'pointer' : 'default',
        }}
        onClick={handleClick}
      />
      {agentSourceIcon && (
        <EdgeLabelRenderer>
          <SessionLabel
            onClick={handleClick}
            isClickable={!!toggleSessionCollapse}
            isCollapsed={session?.isCollapsed}
            style={{
              transform: `translate(-50%, -50%) translate(${labelX - 100}px,${sourceY + 20}px)`,
            }}
            color={color}
          >
            <Stack gap={5} align="flex-start">
              <Group gap="xl">
                {agentSourceIcon}
                {toggleSessionCollapse && (
                  <CollapseIndicator isCollapsed={session?.isCollapsed ?? false} color={color}>
                    {session?.isCollapsed ? '▶' : '▼'}
                  </CollapseIndicator>
                )}
              </Group>

              <Stack justify="space-between" gap={1}>
                <Text size="small">User Prompts: {session?.userPromptCount ?? 0}</Text>
                <Text size="small">Tool Calls: {session?.toolCallCount ?? 0}</Text>
              </Stack>
            </Stack>
          </SessionLabel>
        </EdgeLabelRenderer>
      )}
    </>
  );
};

const SessionLabel = styled.div<{ isClickable?: boolean; isCollapsed?: boolean; color: string }>`
  position: absolute;
  pointer-events: all;
  z-index: 10;
  width: 150px;
  padding: 6px 8px;
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.95);
  border: 2px solid ${({ color }) => color};
  font-size: 11px;
  color: ${({ color }) => color};
  white-space: nowrap;
  box-shadow: 0 2px 6px rgba(35, 193, 107, 0.15);
  align-items: center;
  justify-content: center;
  cursor: ${({ isClickable }) => (isClickable ? 'pointer' : 'default')};
  display: flex;
  gap: 8px;
  transition: all 0.2s ease;
  opacity: ${({ isCollapsed }) => (isCollapsed ? 0.7 : 1)};
  
  &:hover {
    ${({ isClickable }) =>
      isClickable
        ? `
      box-shadow: 0 4px 12px rgba(35, 193, 107, 0.25);
      transform: translateY(-1px);
    `
        : ''}
  }
`;

const CollapseIndicator = styled.span<{ isCollapsed: boolean; color: string }>`
  font-size: 10px;
  color: ${({ color }) => color};
  margin-left: 4px; 
  user-select: none;
  transition: transform 0.2s ease;
`;
